<template>
    <div class="tile">
        <div class="tile-content" v-if="!isPaypalGateway">
            <div class="grid-content">
                <div class="grid-item grid-item-half grid-item-break-half">
                    <v-select
                        label="Select a payment method"
                        :options="methodOptions"
                        :value="method"
                        @input="selectMethod"
                    />
                </div>
            </div>
        </div>
        <div class="tile-content" v-if="isPaymentCardMethod">
            <div class="grid-content">
                <div class="grid-item grid-item-full">
                    <label class="typo__label stack-m">Select the payment card brands you wish to support</label>
                    <div class="grid-content">
                        <div
                            class="grid-item grid-item-third"
                            v-for="(paymentCard) in paymentCardScheme" :key="paymentCard.brand">
                            <r-checkbox
                                :label="paymentCard.brand"
                                :value="paymentCard.brand"
                                :selected="selectedBrands"
                                :disabled="isSpecialGateway"
                                @input="selectCardBrand"
                            />
                        </div>
                    </div>
                </div>
                <div v-if="$v.selectedBrands.$error" class="grid-item grid-item-full">
                    <v-error-messages>
                        <li class="list-item">Please select at least one payment card brand to proceed.</li>
                    </v-error-messages>
                </div>
            </div>
        </div>
        <div class="tile-content" v-if="isPaypalMethod">
            <h2 class="stack-m">Special Instructions</h2>
            <p class="stack-m">To process Reference Transactions, you'll need to enable this feature on both your
                <strong>Payflow</strong> and <strong>PayPal</strong> accounts. You must have admin permissions to
                perform these steps.</p>
            <h3 class="stack-m">Payflow account</h3>
            <ul class="list stack-m">
                <li class="list-item">Log in to PayPal Manager.</li>
                <li class="list-item">Click Account Administration.</li>
                <li class="list-item">Click Transaction Settings.</li>
                <li class="list-item">Set Allow Reference Transactions to Yes.</li>
                <li class="list-item">Click Confirm.</li>
            </ul>
            <p class="text-muted stack-l">Note: Changes may take up to an hour to take effect.</p>
            <h3 class="stack-m">PayPal account</h3>
            <ul class="list stack-m">
                <li class="list-item">Sandbox</li>
                <ul class="stack-m">
                    <li class="list-item">Reference Transactions are automatically enabled on Sandbox accounts created after December 15, 2015.</li>
                    <li class="list-item">If your Sandbox account was created before this date, please contact Merchant
                        Technical Support and request to have Reference Transactions enabled on your Sandbox account.
                    </li>
                </ul>
                <li class="list-item">Live / Production</li>
                <ul>
                    <li class="list-item">Approval from PayPal is required to enable Reference Transactions for your live account.</li>
                    <li class="list-item">To request approval for your live account, contact PayPal Customer Support.
                        Merchant Technical Support does not enable reference transactions for live PayPal accounts.
                    </li>
                </ul>
            </ul>
        </div>
    </div>
</template>
<script>
    import {schema} from '@/common/spec/spec-cache';
    import {vSelect, vErrorMessages} from '@/components/form';
    import validationMixins from './validation-mixins';
    import {
        gatewayIdentifiers,
        paymentCardSchemes,
        methodSchemes,
        paymentMethodIdentifiers,
    } from '@/modules/gateway-accounts/special-config';


    export default {
        name: 'payment-method-selection',
        mixins: [validationMixins],
        components: {vSelect, vErrorMessages},
        props: {
            method: {
                type: String,
            },
            gatewayName: {
                type: String,
                default: null,
            },
            paymentCardSchemes: {
                type: Array,
            },
        },
        computed: {
            methods() {
                return [...schema.Method.enum];
            },
            methodOptions() {
                let methodList = this.methods;
                // check for method scheme overrides from the special config
                if (this.gatewayName in methodSchemes) {
                    methodList = methodSchemes[this.gatewayName];
                }
                return methodList.map(method => ({label: method, value: method}));
            },
            isPaymentCardMethod() {
                return this.localMethod === paymentMethodIdentifiers.paymentCard;
            },
            paymentCardScheme() {
                if (this.gatewayName) {
                    if (this.gatewayName in paymentCardSchemes) {
                        return [...paymentCardSchemes[this.gatewayName]];
                    }
                    return [...paymentCardSchemes.default];
                }
                return [];
            },
            /**
             * If the gateway name matches a value in `gatewayIdentifiers` then disable the checkboxes
             * and prevent the user from changing his selection. This is because those gateways
             * only support the payment card brand defined within and nothing else.
             * @returns {boolean}
             */
            isSpecialGateway() {
                return Object.values(gatewayIdentifiers).includes(this.gatewayName);
            },
            isPaypalGateway() {
                return this.gatewayName === gatewayIdentifiers.payPal;
            },
            isPaypalMethod() {
                return this.method === paymentMethodIdentifiers.paypal;
            },
        },
        data() {
            return {
                localMethod: this.method,
                selectedBrands: this.paymentCardSchemes,
            };
        },
        methods: {
            selectMethod(method) {
                this.localMethod = method;
                if (!this.isPaymentCardMethod) {
                    this.selectedBrands = null;
                }
                this.setDefaultCardSelection();
                this.dispatch();
            },
            selectCardBrand(value) {
                this.$v.$touch();
                this.selectedBrands = value;
                this.dispatch();
            },
            setDefaultCardSelection() {
                if (this.isPaymentCardMethod && this.selectedBrands === null) {
                    this.selectedBrands = this.paymentCardScheme
                        .filter(paymentCard => paymentCard.default)
                        .map(paymentCard => paymentCard.brand);
                    this.dispatch();
                }
            },
            dispatch() {
                this.$emit('input', this.getPayload());
            },
            getPayload() {
                return {
                    method: this.localMethod,
                    scheme: this.selectedBrands ? [...this.selectedBrands] : [],
                };
            },
            validate() {
                this.$v.$touch();
                return this.$v.selectedBrands.$error;
            },
            getValidationAggregatorDefinition() {
                return {
                    name: 'payment-card-method',
                    rules: [
                        {
                            name: 'At least one payment card brand must be selected.',
                            delegate: () => this.validate(),
                        },
                    ],
                };
            },
        },
        validations() {
            return {
                selectedBrands: {
                    isNotEmptyIfRequired: (value) => {
                        if (this.isPaymentCardMethod) {
                            return value && value.length;
                        }
                        return true;
                    },
                },
            };
        },
        created() {
            this.setDefaultCardSelection();
        },
    };
</script>
